import { type ReactNode } from 'react';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { useIdentify } from '@hungryroot/customer';
import { Supplements } from '@hungryroot/icons';
import { useNextOrderV2 } from '@hungryroot/orders';
import { Heading } from '@hungryroot/ramen';
import { StatusCode } from '@hungryroot/rest';
import {
  getApiV2PricingServiceCustomersCustomerIdProductSubscriptionCustomerConfig,
  getPricingServiceCustomerProductSubscriptionCustomerConfigPath,
  type PricingServiceCustomerProductSubscriptionCustomerConfigParams,
} from '@hungryroot/rest/api/v2/pricing_service/customers/[id]/product_subscription_customer_config/get';
import styles from './usePdpSubscriptionBanner.module.css';

const timeLimit = 15 * 60 * 1000;

function useGetCustomerProductSubscriptionConfig(
  params: PricingServiceCustomerProductSubscriptionCustomerConfigParams,
  options?: Pick<UseQueryOptions, 'enabled'>
) {
  return useQuery({
    queryKey: [
      getPricingServiceCustomerProductSubscriptionCustomerConfigPath(params),
    ],
    queryFn: async () => {
      const resp =
        await getApiV2PricingServiceCustomersCustomerIdProductSubscriptionCustomerConfig(
          params
        );

      if (resp.status !== StatusCode.SuccessOK) {
        throw new Error(JSON.stringify(resp.data, null, 2));
      }

      return resp.data;
    },
    ...options,
    retry: false,
    enabled: options?.enabled,
    gcTime: timeLimit,
    staleTime: timeLimit,
  });
}

const SUPPLEMENTS_CONTAINER_ID = 10;

export function usePdpSubscriptionBanner(
  productId?: number
): ReactNode | undefined {
  const { customerData } = useIdentify();
  const { id: customerId } = customerData || {};

  const { order: nextEditableOrder } = useNextOrderV2();

  const { data } = useGetCustomerProductSubscriptionConfig(
    {
      origin: nextEditableOrder?.origin,
      inventory_date:
        nextEditableOrder !== undefined
          ? format(parseISO(nextEditableOrder.cutoff_date), 'yyyy-MM-dd')
          : '',
    },
    {
      enabled: customerId !== undefined && customerId > 0,
    }
  );

  if (customerId === undefined || productId === undefined) {
    return;
  }

  const subscription = data?.results[0];

  if (
    subscription === undefined ||
    subscription.container_id !== SUPPLEMENTS_CONTAINER_ID
  ) {
    return;
  }

  const subscriptionElements = subscription.elements;

  const isProductInSubscription = subscriptionElements.some(
    (element) => element.product.id === productId
  );

  if (!isProductInSubscription) {
    return;
  }

  return (
    <div
      className={styles['supplements-banner']}
      data-testid={`supplements-banner-pdp-${productId}`}
    >
      <Heading variant="H6" className={styles['supplements-heading']}>
        <Supplements className={styles.supplements} />
        This item ships with your monthly supplements
      </Heading>
    </div>
  );
}
